/**
 * Chartkick Stimulus Controller
 *
 * A Stimulus controller that renders charts using the Chartkick library.
 * This controller is designed to work with Turbo Stream updates and provides
 * automatic chart cleanup when elements are removed from the DOM.
 *
 * Usage:
 *   Add the controller to any element with chart data attributes:
 *
 *   <div data-controller="chartkick"
 *        data-chartkick-type-value="LineChart"
 *        data-chartkick-data-value='[["Jan", 100], ["Feb", 200]]'
 *        data-chartkick-options-value='{"legend": {"position": "top"}}'>
 *   </div>
 *
 * Example from revenue and gross profit graph:
 *   tag.div id: "revenue-and-gross-profit-graph",
 *     data: {
 *       controller: 'chartkick',
 *       chartkick_type_value: 'LineChart',
 *       chartkick_data_value: chart_data.to_json,
 *       chartkick_options_value: chart_options.to_json
 *     }
 *
 * Turbo Stream Compatibility:
 *   - Automatically creates charts when elements are added to the DOM
 *   - Cleans up charts when elements are removed (prevents memory leaks)
 *   - Works seamlessly with turbo_stream_replace operations
 *
 * Supported Chart Types:
 *   - LineChart, AreaChart, ColumnChart, BarChart, PieChart, DonutChart,
 *     ScatterChart, GeoChart, Timeline
 *
 * Data Values:
 *   - type: Chart type (String) - must match a Chartkick chart class
 *   - data: Chart data (Array) - series data in Chartkick format
 *   - options: Chart options (Object) - Chartkick configuration options
 */

import { Controller } from "@hotwired/stimulus"

// TODO: It would be useful if we didn't need to include this in all the pages,
// but it has to be included before the Chartkick controller is loaded.
import "chartkick/chart.js"

// Global resize handler to redraw all charts when window is resized
document.addEventListener('resized', ((event) => {
  Chartkick.eachChart(chart => chart.redraw());
}));

export default class extends Controller {
  static values = {
    type:    String,  // Chart type (e.g. "LineChart", "ColumnChart")
    data:    Array,   // Chart data series in Chartkick format
    options: Object   // Chart configuration options
  }

  /**
   * Creates and renders the chart when the controller connects to the DOM.
   * This happens automatically when the element is added via Turbo Stream.
   */
  connect () {
    this.chart = new window.Chartkick[this.typeValue](
      this.element,
      this.dataValue,
      this.optionsValue
    )
  }

  /**
   * Destroys the chart when the controller disconnects from the DOM.
   * This prevents memory leaks when elements are removed via Turbo Stream.
   */
  disconnect () {
    this.chart?.destroy()
  }
}
